import React, { useState, useEffect } from 'react';
import { IonIcon, IonButton, IonRadioGroup, IonRadio, IonLabel, IonItem, IonCheckbox } from '@ionic/react';
import { close } from 'ionicons/icons';
import Modal from './Modal';
import './AETNARequiredInformationSharingConsentModal.css';

interface AETNARequiredInformationSharingConsentModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback fired when modal should close */
  onClose: () => void;
  /** Callback fired when user confirms, receives true if consent selected (keep on), false if decline selected (turn off) */
  onConfirm: (shouldKeepOn: boolean) => void;
  /** Additional CSS class name */
  className?: string;
}

function AETNARequiredInformationSharingConsentModal({
  isOpen,
  onClose,
  onConfirm,
  className = '',
}: AETNARequiredInformationSharingConsentModalProps) {
  const [emergencyResponseNotificationPreference, setEmergencyResponseNotificationPreference] = useState<string>('');
  const [informationSharingPreference, setInformationSharingPreference] = useState<string>('');
  const [checkbox1, setCheckbox1] = useState<boolean>(false);
  const [checkbox2, setCheckbox2] = useState<boolean>(false);
  const [checkbox3, setCheckbox3] = useState<boolean>(false);
  const [checkbox4, setCheckbox4] = useState<boolean>(false);

  // Check if questions are answered, confirming or declining consent.
  const isQuestion1AnsweredNo = emergencyResponseNotificationPreference === 'decline';
  const isQuestion1AnsweredYes = emergencyResponseNotificationPreference === 'consent';
  const isQuestion2Unanswered = informationSharingPreference === '';
  const isQuestion2AnsweredYes = informationSharingPreference === 'consent';

  // Check if all checkboxes are checked
  const areAllCheckboxesChecked = checkbox1 && checkbox2 && checkbox3 && checkbox4;

  // Radio buttons in question 2 are enabled only if all checkboxes are checked
  const areQuestion2RadioButtonsEnabled = areAllCheckboxesChecked;

  // Determine if confirm button should be disabled
  const isConfirmButtonDisabled = () =>   {
    if (isQuestion1AnsweredNo) {
      return false;
    }

    if (isQuestion1AnsweredYes) {
      if (!areAllCheckboxesChecked || isQuestion2Unanswered) {
        return true;
      }
    }

    return false;
  };

  // Reset to no selection when modal opens
  useEffect(() => {
    if (isOpen) {
      setEmergencyResponseNotificationPreference('');
      setInformationSharingPreference('');
      setCheckbox1(false);
      setCheckbox2(false);
      setCheckbox3(false);
      setCheckbox4(false);
    }
  }, [isOpen]);

  // Reset question 2 selections when user changes from "Yes" to "No" in question 1
  useEffect(() => {
    if (!isQuestion1AnsweredYes) {
      setInformationSharingPreference('');
      setCheckbox1(false);
      setCheckbox2(false);
      setCheckbox3(false);
      setCheckbox4(false);
    }
  }, [isQuestion1AnsweredYes]);

  // Reset radio button selection when checkboxes become unchecked
  useEffect(() => {
    if (!areAllCheckboxesChecked) {
      setInformationSharingPreference('');
    }
  }, [areAllCheckboxesChecked]);

  const handleConfirm = () => {
    // If consent is selected, keep toggle on (true), otherwise turn it off (false)
    // TODO: Consider how the toggle button should be updated based on user choices...
    // Should it be "ON" if any of the options are selected?
    const shouldKeepOn = isQuestion1AnsweredYes && areAllCheckboxesChecked && isQuestion2AnsweredYes;

    onConfirm(shouldKeepOn);
    onClose();
  };

  const handleClose = () => {
    // When X is clicked, close modal without making any changes
    onClose();
  };


  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className={className}
    >
      <div className="aetna-required-information-sharing-modal-content">
        <IonButton onClick={handleClose} className="aetna-required-information-sharing-modal-close-button">
          <IonIcon icon={close} />
        </IonButton>

        <div className="aetna-required-information-sharing-modal-heading">
          Optional Information Sharing Consent
        </div>
        <div className="aetna-required-information-sharing-modal-text">
          Optional Information Sharing is an automatic system that sends a notification to Allina Health | AETNA if 
          your conversation with LoreBot triggers an emergency message. Its default setting is turned off, unless you activate it. 
          It does not get you emergency help or urgent care. Your LoreBot chats remain private. Lore is not part of follow-up 
          action taken by Allina Health | AETNA. Contact Allina Health | AETNA if you have questions about what they do with 
          notifications from Lore's Optional Information Sharing system.
        </div>

        <div className="aetna-required-information-sharing-modal-text">
          Optional Information Sharing Does Not:
          <ul>
            <li>
              Call 911 or 988, or other emergency services.
            </li>
            <li>
              Have humans watching it live or in real-time.
            </li>
            <li>
              See or share messages from LoreBot chats.
            </li>
            <li>
              Contact you about emergency messages.  
            </li>
          </ul>
        </div>
        <div className="aetna-required-information-sharing-modal-text">
          <strong>You can change your choice or opt-out at any time.</strong>
          <ol>
            <li>
              By using Optional Information Sharing, you consent to:
              <ul>
                <li>
                  Lore sending a notification to Allina Health | AETNA with your name and 
                  email address if your chat with LoreBot triggers an emergency message.
                </li>
              </ul>
              <div className="aetna-required-information-sharing-modal-radio-group">
                <IonRadioGroup value={emergencyResponseNotificationPreference} onIonChange={(e) => setEmergencyResponseNotificationPreference(e.detail.value)}>
                  <IonItem lines="none" className="aetna-required-information-sharing-modal-radio-item">
                    <IonRadio slot="start" value="decline" />
                    <IonLabel><strong>No</strong> do not send a notification.</IonLabel>
                  </IonItem>
                  <IonItem lines="none" className="aetna-required-information-sharing-modal-radio-item">
                    <IonRadio slot="start" value="consent" />
                    <IonLabel><strong>Yes</strong> send a notification.</IonLabel>
                  </IonItem>
                </IonRadioGroup>
              </div>
            </li>
            <li>
              By using Optional Information Sharing, you acknowledge and agree that:
            </li>
          </ol>
          <div className="aetna-required-information-sharing-modal-checkbox-group">
            <IonItem lines="none" className={`aetna-required-information-sharing-modal-checkbox-item ${!isQuestion1AnsweredYes ? 'checkbox-disabled' : ''}`}>
              <IonCheckbox slot="start" checked={checkbox1} disabled={!isQuestion1AnsweredYes} onIonChange={(e) => setCheckbox1(e.detail.checked)} />
              <IonLabel className="aetna-required-information-sharing-modal-checkbox-label">
                Lore does not monitor LoreBot chats or know if you may be in an emergency.
              </IonLabel>
            </IonItem>
            <IonItem lines="none" className={`aetna-required-information-sharing-modal-checkbox-item ${!isQuestion1AnsweredYes ? 'checkbox-disabled' : ''}`}>
              <IonCheckbox slot="start" checked={checkbox2} disabled={!isQuestion1AnsweredYes} onIonChange={(e) => setCheckbox2(e.detail.checked)} />
              <IonLabel className="aetna-required-information-sharing-modal-checkbox-label">
                Lore is not responsible for taking action to get help if you are in an emergency.
              </IonLabel>
            </IonItem>
            <IonItem lines="none" className={`aetna-required-information-sharing-modal-checkbox-item ${!isQuestion1AnsweredYes ? 'checkbox-disabled' : ''}`}>
              <IonCheckbox slot="start" checked={checkbox3} disabled={!isQuestion1AnsweredYes} onIonChange={(e) => setCheckbox3(e.detail.checked)} />
              <IonLabel className="aetna-required-information-sharing-modal-checkbox-label">
                Lore has no control over Allina Health | AETNA's response to notifications.
              </IonLabel>
            </IonItem>
            <IonItem lines="none" className={`aetna-required-information-sharing-modal-checkbox-item ${!isQuestion1AnsweredYes ? 'checkbox-disabled' : ''}`}>
              <IonCheckbox slot="start" checked={checkbox4} disabled={!isQuestion1AnsweredYes} onIonChange={(e) => setCheckbox4(e.detail.checked)} />
              <IonLabel className="aetna-required-information-sharing-modal-checkbox-label">
                You release Lore from liability for your information sharing actions.
              </IonLabel>
            </IonItem>
          </div>
          <div className="aetna-required-information-sharing-modal-radio-group">
            <IonRadioGroup value={informationSharingPreference} onIonChange={(e) => setInformationSharingPreference(e.detail.value)}>
              <IonItem lines="none" className={`aetna-required-information-sharing-modal-radio-item ${!areQuestion2RadioButtonsEnabled ? 'radio-disabled' : ''}`}>
                <IonRadio slot="start" value="decline" disabled={!areQuestion2RadioButtonsEnabled} />
                <IonLabel><strong>I do not agree. Do not activate</strong> Optional Information Sharing.</IonLabel>
              </IonItem>
              <IonItem lines="none" className={`aetna-required-information-sharing-modal-radio-item ${!areQuestion2RadioButtonsEnabled ? 'radio-disabled' : ''}`}>
                <IonRadio slot="start" value="consent" disabled={!areQuestion2RadioButtonsEnabled} />
                <IonLabel><strong>I agree. Activate</strong> Optional Information Sharing.</IonLabel>
              </IonItem>
            </IonRadioGroup>
          </div>
          <div className="aetna-required-information-sharing-modal-button-wrapper">
            <IonButton
              className="aetna-required-information-sharing-modal-confirm-button"
              disabled={isConfirmButtonDisabled()}
              onClick={handleConfirm}
            >
              Confirm
            </IonButton>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default AETNARequiredInformationSharingConsentModal;

